import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import { Link, withRouter } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { withTranslation } from "react-i18next";
import dayjs from "dayjs";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import DeleteModal from "../../Components/Common/DeleteModal";
import {
  useCreateProductCategory,
  useDeleteProductCategory,
  useProductCategories,
  useUpdateProductCategory,
} from "../../hooks/usePromotedProducts";
import { resolveApiErrorMessage } from "../../utils/resolveApiErrorMessage";

const ProductCategories = (props) => {
  const categoriesQ = useProductCategories();
  const createMutation = useCreateProductCategory();
  const updateMutation = useUpdateProductCategory();
  const deleteMutation = useDeleteProductCategory();

  const [editTarget, setEditTarget] = useState(null);
  const [designation, setDesignation] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const categories = useMemo(() => {
    const data = categoriesQ.data;
    return Array.isArray(data) ? data : data?.results ?? [];
  }, [categoriesQ.data]);

  const openCreate = () => {
    setEditTarget(null);
    setDesignation("");
    setShowForm(true);
  };

  const openEdit = (category) => {
    setEditTarget(category);
    setDesignation(category.designation);
    setShowForm(true);
  };

  const handleSubmit = () => {
    const value = designation.trim();
    if (!value) return;

    const options = { onSettled: () => setShowForm(false) };

    if (editTarget) {
      updateMutation.mutate({ id: editTarget.id, designation: value }, options);
    } else {
      createMutation.mutate(value, options);
    }
  };

  const handleDelete = () => {
    if (!deleteTarget?.id) return;
    deleteMutation.mutate(deleteTarget.id, {
      onSettled: () => setDeleteTarget(null),
    });
  };

  const saving = createMutation.isPending || updateMutation.isPending;

  document.title = props.t("Catégories de produits");

  return (
    <div className="page-content">
      <ToastContainer closeButton={false} />

      <DeleteModal
        show={Boolean(deleteTarget)}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteTarget(null)}
      />

      <Modal isOpen={showForm} toggle={() => setShowForm(false)} centered>
        <ModalHeader toggle={() => setShowForm(false)}>
          {editTarget ? "Modifier la catégorie" : "Nouvelle catégorie"}
        </ModalHeader>
        <ModalBody>
          <Label htmlFor="designation">Désignation</Label>
          <Input
            id="designation"
            type="text"
            value={designation}
            placeholder="Ex. Ciment & Liants"
            onChange={(e) => setDesignation(e.target.value)}
          />
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn btn-light"
            onClick={() => setShowForm(false)}
          >
            Annuler
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={saving || !designation.trim()}
          >
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </ModalFooter>
      </Modal>

      <Container fluid>
        <BreadCrumb
          title={props.t("Catégories de produits")}
          pageTitle={props.t("Promotion de produits")}
        />

        <Row>
          <Col xl={8}>
            <Card>
              <CardBody className="border-bottom d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0">
                  {categories.length} catégorie
                  {categories.length > 1 ? "s" : ""}
                </h5>
                <div className="d-flex gap-2">
                  <Link to="/promoted-products" className="btn btn-light">
                    Tous les produits
                  </Link>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={openCreate}
                  >
                    <i className="ri-add-line align-bottom me-1" />
                    Nouvelle catégorie
                  </button>
                </div>
              </CardBody>

              <CardBody>
                {categoriesQ.isLoading ? (
                  <div className="placeholder-glow" style={{ height: 120 }}>
                    <span className="placeholder col-12 h-100" />
                  </div>
                ) : categoriesQ.isError ? (
                  <div className="text-center py-4">
                    <h5>
                      {resolveApiErrorMessage(
                        categoriesQ.error,
                        "Une erreur est survenue"
                      )}
                    </h5>
                    <button
                      type="button"
                      className="btn btn-primary mt-2"
                      onClick={() => categoriesQ.refetch()}
                    >
                      Réessayer
                    </button>
                  </div>
                ) : categories.length === 0 ? (
                  <div className="text-center py-5">
                    <h5>Aucune catégorie</h5>
                    <button
                      type="button"
                      className="btn btn-primary mt-2"
                      onClick={openCreate}
                    >
                      Créer une catégorie
                    </button>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle mb-0">
                      <thead className="table-light text-muted">
                        <tr>
                          <th>DÉSIGNATION</th>
                          <th>CRÉÉE LE</th>
                          <th>ACTIONS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {categories.map((category) => (
                          <tr key={category.id}>
                            <td className="fw-semibold">
                              {category.designation}
                            </td>
                            <td className="text-muted">
                              {category.created_at
                                ? dayjs(category.created_at).format("DD/MM/YYYY")
                                : "—"}
                            </td>
                            <td>
                              <ul className="list-inline mb-0">
                                <li className="list-inline-item">
                                  <button
                                    type="button"
                                    className="btn btn-link text-warning p-0"
                                    title="Modifier"
                                    onClick={() => openEdit(category)}
                                  >
                                    <i className="ri-pencil-fill align-bottom" />
                                  </button>
                                </li>
                                <li className="list-inline-item">
                                  <button
                                    type="button"
                                    className="btn btn-link text-danger p-0"
                                    title="Supprimer"
                                    onClick={() => setDeleteTarget(category)}
                                  >
                                    <i className="ri-delete-bin-5-line align-bottom" />
                                  </button>
                                </li>
                              </ul>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <small className="text-muted d-block mt-2">
                      Une catégorie contenant des produits ne peut pas être
                      supprimée.
                    </small>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

ProductCategories.propTypes = {
  t: PropTypes.any,
};

export default withRouter(withTranslation()(ProductCategories));
